import { useEffect, useState, useCallback } from 'react';
import { api, apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  PencilEdit01Icon,
  Delete02Icon,
  Add01Icon,
  Route01Icon,
  UserMultipleIcon,
  Shield01Icon,
} from '@hugeicons/core-free-icons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

export function ProfilesTab() {
  const [profiles, setProfiles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [routeCatalog, setRouteCatalog] = useState([]);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formName, setFormName] = useState('');
  const [formRouteIds, setFormRouteIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/profiles?page=${page}`);
      setProfiles(data.profiles || []);
      setTotalPages(data.total_pages || 1);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [page]);

  const fetchRouteCatalog = useCallback(async () => {
    try {
      const { data } = await api.get('/route-catalog');
      // API returns object grouped by category: { "Nucleo": [...], "Toolkit": [...] }
      // Flatten to a single array of routes
      if (Array.isArray(data)) {
        setRouteCatalog(data);
      } else if (data && typeof data === 'object') {
        const flattened = Object.values(data).flat();
        setRouteCatalog(flattened);
      } else {
        setRouteCatalog([]);
      }
    } catch (e) {
      toast.error('Error cargando catálogo de rutas');
    }
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    fetchRouteCatalog();
  }, [fetchRouteCatalog]);

  const openCreateDialog = () => {
    setEditingProfile(null);
    setFormName('');
    setFormRouteIds([]);
    setIsDialogOpen(true);
  };

  const openEditDialog = (profile) => {
    setEditingProfile(profile);
    setFormName(profile.name);
    setFormRouteIds(profile.routes?.map((r) => r.id) || []);
    setIsDialogOpen(true);
  };

  const handleRouteToggle = (routeId) => {
    setFormRouteIds((prev) => (prev.includes(routeId) ? prev.filter((id) => id !== routeId) : [...prev, routeId]));
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    if (formRouteIds.length === 0) {
      toast.error('Seleccione al menos una ruta');
      return;
    }

    setSaving(true);
    try {
      if (editingProfile) {
        await api.put(`/admin/profiles/${editingProfile.id}`, {
          name: formName.trim(),
          route_ids: formRouteIds,
        });
        toast.success('Perfil actualizado');
      } else {
        await api.post('/admin/profiles', {
          name: formName.trim(),
          route_ids: formRouteIds,
        });
        toast.success('Perfil creado');
      }
      setIsDialogOpen(false);
      fetchProfiles();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/profiles/${deleteTarget.id}`);
      toast.success('Perfil eliminado');
      setDeleteTarget(null);
      fetchProfiles();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Group routes by category
  const routesByCategory = routeCatalog.reduce((acc, route) => {
    const cat = route.category || 'Sin categoría';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(route);
    return acc;
  }, {});

  // Derive route categories per profile for display
  const getProfileCategories = (profile) => {
    if (!profile.routes || profile.routes.length === 0) return [];
    const cats = [...new Set(profile.routes.map((r) => r.category))];
    return cats.slice(0, 3); // Show max 3 categories
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Perfiles de permisos</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            Define conjuntos de rutas que puedes asignar a múltiples usuarios.
          </p>
        </div>
        <Button onClick={openCreateDialog} className="gap-2" data-testid="create-profile-btn">
          <HugeiconsIcon icon={Add01Icon} className="h-4 w-4" />
          Crear Perfil
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-5">
                <div className="h-5 bg-muted rounded w-1/3 mb-3" />
                <div className="h-3 bg-muted rounded w-2/3 mb-4" />
                <div className="flex gap-4">
                  <div className="h-4 bg-muted rounded w-16" />
                  <div className="h-4 bg-muted rounded w-16" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : profiles.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 mb-4">
              <HugeiconsIcon icon={Shield01Icon} className="h-6 w-6 text-primary" />
            </div>
            <p className="text-sm font-medium">No hay perfiles configurados</p>
            <p className="text-xs text-muted-foreground mt-1">
              Cree un perfil para agrupar rutas y asignarlas a usuarios.
            </p>
            <Button onClick={openCreateDialog} className="gap-2 mt-4" size="sm">
              <HugeiconsIcon icon={Add01Icon} className="h-3.5 w-3.5" />
              Crear primer perfil
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="profiles-table">
          {profiles.map((profile) => (
            <Card key={profile.id} className="hover:border-primary/30 transition-colors">
              <CardContent className="p-4 flex items-center gap-4">
                {/* Left: icon + name + categories */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <HugeiconsIcon icon={Shield01Icon} className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-semibold truncate">{profile.name}</h4>
                    <p className="text-[11px] text-muted-foreground truncate mt-0.5">
                      {getProfileCategories(profile).join(' · ') || 'Sin rutas asignadas'}
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-3 shrink-0">
                  <div className="flex items-center gap-1.5" title={`${profile.routes?.length || 0} rutas registradas`}>
                    <HugeiconsIcon icon={Route01Icon} className="h-3.5 w-3.5 text-secondary" />
                    <span className="text-xs font-medium">{profile.routes?.length || 0}</span>
                  </div>
                  <div className="flex items-center gap-1.5" title={`${profile.user_count || 0} usuarios asociados`}>
                    <HugeiconsIcon icon={UserMultipleIcon} className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-xs font-medium">{profile.user_count || 0}</span>
                  </div>
                </div>

                {/* Actions — always visible, icon-only */}
                <div className="flex items-center gap-0.5 shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => openEditDialog(profile)}
                    title="Editar perfil"
                    aria-label={`Editar perfil ${profile.name}`}
                    data-testid={`edit-profile-${profile.id}`}>
                    <HugeiconsIcon icon={PencilEdit01Icon} className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    onClick={() => setDeleteTarget(profile)}
                    title="Eliminar perfil"
                    aria-label={`Eliminar perfil ${profile.name}`}
                    data-testid={`delete-profile-${profile.id}`}>
                    <HugeiconsIcon icon={Delete02Icon} className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Página {page} de {totalPages}
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Siguiente
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProfile ? 'Editar Perfil' : 'Crear Perfil'}</DialogTitle>
            <DialogDescription>
              {editingProfile
                ? 'Modifique el nombre y las rutas asignadas al perfil.'
                : 'Defina un nombre y seleccione las rutas que incluirá este perfil.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="profile-name">Nombre del perfil</Label>
              <Input
                id="profile-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Ej: Evaluador de riesgos"
                maxLength={100}
                data-testid="profile-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Rutas habilitadas ({formRouteIds.length} seleccionadas)</Label>
              <div className="border rounded-md p-3 space-y-4 max-h-60 overflow-y-auto">
                {Object.entries(routesByCategory).map(([category, routes]) => (
                  <div key={category}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                      {category}
                    </p>
                    <div className="space-y-1.5">
                      {routes.map((route) => (
                        <label
                          key={route.id}
                          className="flex items-center gap-2 cursor-pointer hover:bg-muted/50 rounded px-2 py-1">
                          <Checkbox
                            checked={formRouteIds.includes(route.id)}
                            onCheckedChange={() => handleRouteToggle(route.id)}
                            data-testid={`route-checkbox-${route.id}`}
                          />
                          <span className="text-sm">{route.page_name}</span>
                          <span className="text-xs text-muted-foreground ml-auto">{route.path}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
                {Object.keys(routesByCategory).length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay rutas disponibles en el catálogo
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
              {saving ? 'Guardando...' : editingProfile ? 'Guardar cambios' : 'Crear perfil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar perfil</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.user_count > 0 ? (
                <>
                  Este perfil está asignado a <strong>{deleteTarget.user_count} usuario(s)</strong>. Debe desasignar el
                  perfil de todos los usuarios antes de eliminarlo.
                </>
              ) : (
                <>
                  ¿Está seguro de eliminar el perfil <strong>"{deleteTarget?.name}"</strong>? Esta acción no se puede
                  deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            {deleteTarget?.user_count === 0 && (
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                data-testid="confirm-delete-btn">
                Eliminar
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
